import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Override-modal service.
 *
 * Legacy reference: ui/app/components/efile/EfileService.js
 *   - getSysOverideHis    -> POST /loadJsonObject?action_code=mi11tr
 *   - getSysAmtValue      -> POST /loadJsonObject?action_code=20ovw6
 *   - getSubmissionLocked -> POST /loadJsonObject?action_code=95vnw5
 *   - saveOverride        -> POST /saveJsonObject?action_code=rv8nq0
 *
 * The earlier port pointed at REST endpoints
 * (`<efile-base>/getSysOverideHis`, etc.) which do not exist on this
 * stack — every request 404'd which is why the override history table /
 * system-value radio rendered empty when the edit pencil was clicked.
 * We now mirror the legacy `JsonObjectFactory.getJSONObj` call exactly:
 * POST to the workspace base url with `action_code` + form params, body
 * encoded as `application/x-www-form-urlencoded`.
 */

/** Action codes copied verbatim from `EfileService.js` L13-14. */
const GET_FORM_OVERRIDE_HISTORY = 'mi11tr';
const GET_SYSTEM_AMOUNT_VALUE = '20ovw6';
const GET_SUBMISSION_LOCKED = '95vnw5';
const SAVE_EFILE_ELEMENT_OVERRIDE = 'rv8nq0';

export interface OverrideHistoryParams {
  tax_year: string | number;
  job_id: string | number;
  key_id: string | number;
  element_name: string;
  sno: string | number;
  form_sub: string;
  parent_element_name: string;
  submission_id: string | number;
}

export interface SystemValueParams {
  tax_year: string | number;
  job_id: string | number;
  key_id: string | number;
  element_name: string;
  sno: string | number;
  form_sub: string;
  parent_element_name: string;
}

export interface SaveOverrideParams {
  clob_settings: string;
  tax_year: string | number;
  scenario: string | number;
  jcd_key: number;
  clob_data: string;
  sso_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class OverrideModalService {

  constructor(private http: HttpClient) { }

  /**
   * Helper — POST to `loadJsonObject` / `saveJsonObject` with
   * `application/x-www-form-urlencoded` body. Mirrors `add-edit.service.ts`
   * which already follows the same migration pattern.
   */
  private postForm(url: string, params: Record<string, any>): Observable<any> {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const v = params[key];
      if (v === undefined || v === null) return;
      urlEncoded.set(key, typeof v === 'object' ? JSON.stringify(v) : String(v));
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Override history rows that populate the radio-list table at the bottom
   * of the modal. Legacy: `EfileFactory.getSysOverideHis` (EfileService.js
   * L574-588) — note the legacy field is `sub_form`, NOT `form_sub`.
   */
  getOverrideHistory(baseUrl: string, params: OverrideHistoryParams): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=${GET_FORM_OVERRIDE_HISTORY}`;
    return this.postForm(url, {
      tax_year: params.tax_year,
      job_id: params.job_id,
      key_id: params.key_id,
      element_name: params.element_name,
      sno: params.sno,
      sub_form: params.form_sub,
      parent_element_name: params.parent_element_name,
      submission_id: params.submission_id,
    });
  }

  /**
   * Backend-computed "system" value for the System Value radio. Legacy:
   * `EfileFactory.getSysAmtValue` (EfileService.js L590-602). Same field
   * naming caveat (`sub_form` vs `form_sub`).
   */
  getSystemValue(baseUrl: string, params: SystemValueParams): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=${GET_SYSTEM_AMOUNT_VALUE}`;
    return this.postForm(url, {
      tax_year: params.tax_year,
      job_id: params.job_id,
      key_id: params.key_id,
      element_name: params.element_name,
      sno: params.sno,
      sub_form: params.form_sub,
      parent_element_name: params.parent_element_name,
    });
  }

  /**
   * Submission lock indicator. Legacy: `EfileFactory.getSubmissionLocked`
   * (EfileService.js L603-608).
   */
  getSubmissionLocked(baseUrl: string, submissionId: string | number): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=${GET_SUBMISSION_LOCKED}`;
    return this.postForm(url, { submission_id: submissionId });
  }

  /**
   * Save the override row. Legacy: AddEditController.saveForm flow
   * (EfileElementsController.js L4022) — same `rv8nq0` save action used
   * by the add/edit screen.
   */
  saveOverride(baseUrl: string, params: SaveOverrideParams): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=${SAVE_EFILE_ELEMENT_OVERRIDE}`;
    return this.postForm(url, {
      clob_settings: params.clob_settings,
      tax_year: params.tax_year,
      scenario: params.scenario,
      jcd_key: params.jcd_key,
      clob_data: params.clob_data,
      sso_id: params.sso_id,
    });
  }
}
